import { Navigate } from 'react-router'
import { useAuth } from '../auth/AuthProvider'
import { CenteredFrame } from '../components/AppFrame'
import { ErrorState, FullPageLoading } from '../components/states'
import { SignInScreen } from '../screens/SignInScreen'

/**
 * Route `/auth/callback`.
 *
 * Deliberately thin. It does NOT parse the URL or exchange anything itself —
 * AuthProvider owns that, because the handling must not be bolted to one
 * route.
 *
 * As of the `next`-param task, BOTH Google OAuth and magic link redirect
 * DIRECTLY to `SITE_URL + next` — the rep's original destination — rather
 * than landing here. This route is now a legacy fallback: it is still
 * reached if `next` was absent (no destination to carry) or by a stale
 * bookmark/link, and AuthProvider still handles a session appearing on it
 * the same as on any other route. `authorized` below still redirects to `/`
 * as its own safety net for that fallback case.
 *
 * Both `type=signup` and `type=magiclink` are accepted; see
 * ../auth/callback-params.ts for why that must never be narrowed.
 */
export function AuthCallbackScreen() {
  const { state, emailCode, providerError, signInWithProvider, retry } = useAuth()

  switch (state.status) {
    case 'initializing':
    case 'checking':
      return <FullPageLoading label="Signing you in…" />

    case 'not_authorized':
      return <Navigate to="/not-authorized" replace />

    case 'error':
      return (
        <CenteredFrame>
          <ErrorState title="We could not finish signing you in" detail={state.detail} onRetry={retry} />
        </CenteredFrame>
      )

    case 'signed_out':
      // The link was expired, already used, or the provider refused. Offer the
      // sign-in screen again rather than a dead end — `providerError` carries
      // the callback error when there was one.
      return (
        <SignInScreen
          onProvider={signInWithProvider}
          emailCode={emailCode}
          providerError={providerError}
        />
      )

    case 'authorized':
      // AuthProvider navigates to the return URL on success. This is only the
      // frame between the verdict and that navigation, plus the case where a
      // signed-in rep opens /auth/callback directly.
      return <Navigate to="/" replace />
  }
}
